import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';

import { NotificationServiceHM } from 'src/app/menu/notification-item/notification-item.service';
import { ReviewService } from '../../review.service';
import { getApiErrorMessage } from '../../../core/utils/http-error-message.util';
import {
  FeedbackKind,
  FeedbackResolution,
  ReviewFeedback,
  ReviewFeedbackRequest
} from '../../interfaces/review-feedback.interface';
import {
  FINDING_RESOLUTIONS,
  OVERALL_RESOLUTIONS,
  ResolutionOption,
  resolutionRequiresComment
} from '../../review-feedback.constants';

export interface ReviewFeedbackChange {
  findingId?: string;
  findingKind: FeedbackKind;
  feedback: ReviewFeedback | null;
}

@Component({
  selector: 'app-review-feedback',
  templateUrl: './review-feedback.component.html',
  styleUrls: ['./review-feedback.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewFeedbackComponent implements OnChanges {
  @Input() resourceType = '';
  @Input() resourceId = '';
  @Input() findingKind: FeedbackKind = 'overall';
  @Input() findingId?: string;
  @Input() answerArtifactId?: number;
  @Input() initialResolution: FeedbackResolution | null = null;
  @Input() initialComment?: string;
  @Input() feedbackId?: string;
  /** Ajusta a densidade visual quando renderizado dentro de cards estreitos. */
  @Input() compact = false;

  @Output() feedbackChanged = new EventEmitter<ReviewFeedbackChange>();

  private readonly findingResolutions = FINDING_RESOLUTIONS;
  private readonly overallResolutions = OVERALL_RESOLUTIONS;

  resolutions: ResolutionOption[] = this.findingResolutions;

  selectedResolution: FeedbackResolution | null = null;
  /**
   * Resolução escolhida que ainda não foi gravada: falso positivo e "não vou corrigir"
   * esperam o comentário, e só vão para a API junto com ele.
   */
  pendingResolution: FeedbackResolution | null = null;
  currentFeedbackId?: string;
  comment = '';
  /** O comentário como está gravado: é contra ele que se decide se há o que salvar. */
  savedComment = '';
  showCommentBox = false;
  saving = false;
  commentSaving = false;

  constructor(
    private smartReviewService: ReviewService,
    private notificationServiceHM: NotificationServiceHM,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['findingKind']) {
      this.resolutions = this.findingKind === 'overall' ? this.overallResolutions : this.findingResolutions;
    }
    if (
      changes['initialResolution'] ||
      changes['initialComment'] ||
      changes['feedbackId'] ||
      changes['findingId']
    ) {
      this.selectedResolution = this.initialResolution ?? null;
      this.pendingResolution = null;
      this.currentFeedbackId = this.feedbackId;
      this.comment = this.initialComment ?? '';
      this.savedComment = this.comment;
      this.showCommentBox = !!this.selectedResolution;
    }
  }

  /** Per-achado só renderiza quando há findingId; overall sempre renderiza. */
  get canRender(): boolean {
    return this.findingKind === 'overall' || !!this.findingId;
  }

  get headerLabel(): string {
    return this.findingKind === 'overall' ? 'O que achou do review?' : 'Sua avaliação';
  }

  /**
   * Só o "Pendente" do veredito geral: é o convite a avaliar o review. "Avaliado" repetia o que
   * a opção marcada já mostra.
   */
  get showStatusTag(): boolean {
    return this.findingKind === 'overall' && !this.currentFeedbackId;
  }

  /** O que está marcado na tela: a escolha pendente, se houver, senão a gravada. */
  get activeResolution(): FeedbackResolution | null {
    return this.pendingResolution ?? this.selectedResolution;
  }

  get commentRequired(): boolean {
    return resolutionRequiresComment(this.activeResolution);
  }

  get commentPlaceholder(): string {
    switch (this.activeResolution) {
      case 'false_positive': return 'Por que é falso positivo? (obrigatório)';
      case 'wont_fix':       return 'Motivo da decisão (obrigatório)';
      default:               return 'Algum detalhe a destacar?';
    }
  }

  get saveCommentLabel(): string {
    return this.pendingResolution ? 'Salvar avaliação' : 'Salvar comentário';
  }

  /** Escolha esperando o comentário obrigatório, ou comentário diferente do gravado. */
  get hasUnsavedChanges(): boolean {
    return !!this.pendingResolution || this.comment.trim() !== this.savedComment.trim();
  }

  get canSaveComment(): boolean {
    return !!this.activeResolution && !!this.comment.trim() && !this.commentSaving;
  }

  isSelected(value: FeedbackResolution): boolean {
    return this.activeResolution === value;
  }

  trackByValue(_: number, item: ResolutionOption): string {
    return item.value;
  }

  /**
   * Clique no chip:
   * - na escolha pendente: desiste dela e volta ao que estava gravado;
   * - na já gravada: remove;
   * - em falso positivo / não vou corrigir: só marca e abre o comentário obrigatório;
   * - em procede: grava na hora.
   */
  onResolutionClick(value: FeedbackResolution): void {
    if (this.saving) { return; }
    if (value === this.pendingResolution) {
      // Desistir da escolha devolve o comentário gravado: o motivo digitado para ela não é
      // comentário da avaliação que fica, e "Salvar" o gravaria como se fosse.
      this.pendingResolution = null;
      this.comment = this.savedComment;
      this.showCommentBox = !!this.selectedResolution;
      return;
    }
    if (value === this.selectedResolution) {
      this.pendingResolution = null;
      this.removeFeedback();
      return;
    }
    if (resolutionRequiresComment(value)) {
      this.pendingResolution = value;
      this.showCommentBox = true;
      return;
    }
    this.pendingResolution = null;
    this.saveResolution(value);
  }

  onSaveComment(): void {
    const trimmedComment = this.comment.trim();
    const resolution = this.activeResolution;
    if (!resolution || this.commentSaving || !trimmedComment) { return; }

    const wasPending = !!this.pendingResolution;
    this.commentSaving = true;
    this.smartReviewService
      .saveReviewFeedback(
        this.resourceType,
        this.resourceId,
        this.buildBody(resolution, trimmedComment)
      )
      .pipe(finalize(() => { this.commentSaving = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: feedback => {
          this.selectedResolution = resolution;
          this.pendingResolution = null;
          this.currentFeedbackId = feedback.id;
          this.comment = feedback.comment ?? trimmedComment;
          this.savedComment = this.comment;
          this.notificationServiceHM.showToast(wasPending ? 'Avaliação registrada' : 'Comentário salvo', 'success');
          this.feedbackChanged.emit({ findingId: this.findingId, findingKind: this.findingKind, feedback });
        },
        error: (error: unknown) => {
          this.notificationServiceHM.showToast(this.feedbackErrorMessage(error), 'error');
        }
      });
  }

  onRemoveClick(): void {
    if (this.saving) { return; }
    this.removeFeedback();
  }

  private saveResolution(resolution: FeedbackResolution): void {
    this.saving = true;
    this.smartReviewService
      .saveReviewFeedback(this.resourceType, this.resourceId, this.buildBody(resolution))
      .pipe(finalize(() => { this.saving = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: feedback => {
          this.selectedResolution = resolution;
          this.currentFeedbackId = feedback.id;
          this.comment = feedback.comment ?? this.comment;
          this.savedComment = feedback.comment ?? '';
          this.showCommentBox = true;
          this.notificationServiceHM.showToast('Avaliação registrada', 'success');
          this.feedbackChanged.emit({ findingId: this.findingId, findingKind: this.findingKind, feedback });
        },
        error: (error: unknown) => {
          this.notificationServiceHM.showToast(this.feedbackErrorMessage(error), 'error');
        }
      });
  }

  private removeFeedback(): void {
    this.pendingResolution = null;
    if (!this.currentFeedbackId) {
      this.selectedResolution = null;
      this.showCommentBox = false;
      this.comment = '';
      return;
    }

    const removedResolution = this.selectedResolution;
    const removedComment = this.comment.trim();

    this.saving = true;
    this.smartReviewService
      .deleteReviewFeedback(this.resourceType, this.resourceId, this.currentFeedbackId)
      .pipe(finalize(() => { this.saving = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: () => {
          this.selectedResolution = null;
          this.currentFeedbackId = undefined;
          this.comment = '';
          this.savedComment = '';
          this.showCommentBox = false;

          if (removedResolution) {
            this.notificationServiceHM.showToast('Avaliação removida', 'success', {
              action: {
                label: 'Desfazer',
                callback: () => this.restoreFeedback(removedResolution, removedComment)
              },
              durationMs: 7000
            });
          } else {
            this.notificationServiceHM.showToast('Avaliação removida', 'success');
          }

          this.feedbackChanged.emit({ findingId: this.findingId, findingKind: this.findingKind, feedback: null });
        },
        error: (error: unknown) => {
          this.notificationServiceHM.showToast(this.feedbackErrorMessage(error), 'error');
        }
      });
  }

  private restoreFeedback(resolution: FeedbackResolution, comment: string): void {
    if (this.saving) { return; }

    this.saving = true;
    this.smartReviewService
      .saveReviewFeedback(this.resourceType, this.resourceId, this.buildBody(resolution, comment))
      .pipe(finalize(() => { this.saving = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: feedback => {
          this.selectedResolution = resolution;
          this.currentFeedbackId = feedback.id;
          this.comment = feedback.comment ?? comment;
          this.savedComment = this.comment;
          this.showCommentBox = true;
          this.notificationServiceHM.showToast('Avaliação restaurada', 'success');
          this.feedbackChanged.emit({ findingId: this.findingId, findingKind: this.findingKind, feedback });
        },
        error: (error: unknown) => {
          this.notificationServiceHM.showToast(this.feedbackErrorMessage(error), 'error');
        }
      });
  }

  private buildBody(resolution: FeedbackResolution, comment?: string): ReviewFeedbackRequest {
    if (this.findingKind === 'overall') {
      return { findingKind: 'overall', resolution, ...(comment ? { comment } : {}) };
    }
    return {
      findingKind: this.findingKind,
      findingId: this.findingId!,
      answerArtifactId: this.answerArtifactId!,
      resolution,
      ...(comment ? { comment } : {})
    };
  }

  private feedbackErrorMessage(error: unknown): string {
    // Sempre a mensagem do backend quando vier; senão, fallback por status.
    const status = error instanceof HttpErrorResponse ? error.status : 0;
    let fallback: string;
    switch (status) {
      case 400:
        fallback = 'Tipo ou resolução inválido para o feedback.';
        break;
      case 403:
        fallback = 'Você não tem permissão para registrar este feedback.';
        break;
      case 404:
        fallback = 'Item de review não encontrado para feedback. O review pode ter sido reprocessado.';
        break;
      case 500:
        fallback = 'Erro interno ao salvar feedback. Tente novamente.';
        break;
      default:
        fallback = 'Não foi possível salvar o feedback. Tente novamente.';
    }
    return getApiErrorMessage(error, fallback);
  }
}
